import React from 'react';

import PropTypes from 'prop-types';

import { Body } from '@iris/react-components/Body';
import { Box } from '@iris/react-components/Box';
import { Card } from '@iris/react-components/Card';
import { Divider } from '@iris/react-components/Divider';

import PriceLineItem from '@/components/PriceLineItem';

/**
 * Converts a date string from MM.DD.YYYY format to full month name format
 * @param {string} dateString - Date string in MM.DD.YYYY format (e.g., "09.18.2025")
 * @returns {string} Formatted date string (e.g., "September 18, 2025")
 */
function formatDateString(dateString) {
    if (!dateString || typeof dateString !== 'string') {
        return '';
    }

    try {
        const [month, day, year] = dateString.split('.');

        // Validate the parts exist
        if (!month || !day || !year) {
            return dateString; // Return original if format is invalid
        }

        // Create date object (month is 0-indexed in Date constructor)
        const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));

        // Check if date is valid
        if (Number.isNaN(date.getTime())) {
            return dateString; // Return original if date is invalid
        }

        // Format using toLocaleDateString with options
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    } catch (error) {
        // Return original string if any error occurs
        return dateString;
    }
}

/**
 * Renders a single timeline item displaying a date and price in a two-column grid layout.
 *
 * @param {Object} props - The component props
 * @param {string} props.label - The date label for the timeline item
 * @param {string} props.value - The price value for the timeline item
 * @param {number} props.index - The index position used for data-cmp attribute
 * @returns {JSX.Element} A timeline item with date and price information
 */
function PriceHistoryTimelineItem({ label, value, index }) {
    return (
        <PriceLineItem
            data-cmp={`timeline-item-${index}`}
            label={label}
            value={value}
            containerClassName="iris:py-3"
        />
    );
}

/**
 * PriceHistoryTimeline displays a vehicle's price history in a timeline format with historical entries and current price.
 * It renders historical price data in chronological order with dividers between items and highlights today's price in a special card.
 * The component uses formatDateString to convert MM.DD.YYYY dates to readable format and handles empty data gracefully.
 *
 * @component
 * @param {Object} props - The component props
 * @param {Array} [props.data=[]] - Array of price history objects with label (date) and value (price) properties
 * @returns {JSX.Element|null} The price timeline component or null if no data provided
 */
function PriceHistoryTimeline({
    data = [],
}) {
    if (data.length === 0) return null;

    const timelineItems = data.slice(0, -1);
    const latestEntry = data[data.length - 1];

    return (
        <Box>
            <Body
                as="p"
                className="iris:mb-6"
            >
                Discover the fluctuations in this vehicle&apos;s price throughout the years.
            </Body>

            <div data-cmp="price-history-timeline">
                {timelineItems.map((item, index) => (
                    <React.Fragment key={index}>
                        <Divider />
                        <PriceHistoryTimelineItem
                            label={formatDateString(item.label)}
                            value={item.value}
                            index={index}
                        />
                    </React.Fragment>
                ))}
            </div>

            <Card className="iris:bg-blue-100 iris:border-0 iris:rounded-lg iris:p-4 iris:h-[77px] iris:flex iris:items-center iris:justify-center iris:mt-2">
                <Card.Content className="iris:m-auto iris:box-border iris:content-center">
                    <div
                        data-cmp="timeline-item-today"
                        className="iris:font-bold iris:text-lg"
                    >
                        Today&apos;s Price:
                        {' '}
                        {latestEntry.value}
                    </div>
                </Card.Content>
            </Card>
        </Box>
    );
}

PriceHistoryTimeline.propTypes /* remove-proptypes */ = {
    /**
     * Array of price history data with label and value properties
     */
    data: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
    })),
};

export default PriceHistoryTimeline;
